/**
 * Per-user MRU ordering for small workshop selectors (#2672).
 *
 * Storage follows the existing localStorage conventions (`vs-` prefix,
 * try/catch, drop the key when empty). Keys include tenant + user so
 * two people on the same browser, or the same person on two tenants,
 * never share a list.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

export const DROPDOWN_MRU_STORAGE_PREFIX = 'vs-dropdown-mru';
export const DROPDOWN_MRU_MAX = 12;
export const MOST_USED_GROUP_LIMIT = 8;

export const DROPDOWN_MRU_SELECTORS = {
  service: 'service',
  mechanic: 'mechanic',
  vat: 'vat',
  unit: 'unit',
  paymentMethod: 'paymentMethod',
  part: 'part',
} as const;

export type DropdownMruSelector =
  (typeof DROPDOWN_MRU_SELECTORS)[keyof typeof DROPDOWN_MRU_SELECTORS];

/**
 * Selectors whose `order()` keeps the caller's sequence (#4110).
 *
 * Mechanic lists arrive already sorted by display name (#3408). Recently
 * used names must not jump ahead of that alphabetical order. `record()`
 * still stores uses; stored ids do not move rows. Add a selector here
 * when its picker must stay alphabetical. Every other selector keeps
 * most-used-first ordering.
 */
export const ALPHABETICAL_ONLY_MRU_SELECTORS: ReadonlySet<string> = new Set<string>([
  DROPDOWN_MRU_SELECTORS.mechanic,
]);

export function dropdownMruStorageKey(
  selector: string,
  userId: string,
  tenantId: string,
): string {
  return `${DROPDOWN_MRU_STORAGE_PREFIX}:${tenantId}:${userId}:${selector}`;
}

export function parseMruIds(raw: string | null): string[] {
  if (raw === null || raw === '') return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string' && id !== '');
  } catch {
    return [];
  }
}

export function readMruIds(storageKey: string): string[] {
  try {
    return parseMruIds(localStorage.getItem(storageKey));
  } catch {
    return [];
  }
}

export function writeMruIds(storageKey: string, ids: readonly string[]): void {
  try {
    if (ids.length === 0) {
      localStorage.removeItem(storageKey);
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify([...ids]));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

export function recordMruId(
  ids: readonly string[],
  id: string,
  max: number = DROPDOWN_MRU_MAX,
): string[] {
  if (id === '') return [...ids];
  return [id, ...ids.filter((existing) => existing !== id)].slice(0, max);
}

export function orderByMru<T>(
  items: readonly T[],
  recentIds: readonly string[],
  getId: (item: T) => string,
): T[] {
  if (recentIds.length === 0) return [...items];
  const rank = new Map(recentIds.map((id, index) => [id, index]));
  return [...items]
    .map((item, index) => ({ item, index, rank: rank.get(getId(item)) }))
    .sort((a, b) => {
      if (a.rank === undefined && b.rank === undefined) return a.index - b.index;
      if (a.rank === undefined) return 1;
      if (b.rank === undefined) return -1;
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.index - b.index;
    })
    .map((entry) => entry.item);
}

function relevanceScore(label: string, query: string): number {
  const haystack = label.toLocaleLowerCase();
  const needle = query.toLocaleLowerCase();
  if (haystack === needle) return 0;
  if (haystack.startsWith(needle)) return 1;
  const index = haystack.indexOf(needle);
  if (index >= 0) return 2 + index;
  return 100;
}

/**
 * Typed search: exact / prefix / substring beats usage and MRU. Equal
 * scores keep the incoming order so keyboard highlight does not jump
 * among ties as the user types.
 */
export function rankByQueryRelevance<T>(
  items: readonly T[],
  query: string,
  getLabel: (item: T) => string,
): T[] {
  const trimmed = query.trim();
  if (trimmed === '') return [...items];
  return [...items]
    .map((item, index) => ({
      item,
      index,
      score: relevanceScore(getLabel(item), trimmed),
    }))
    .sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score;
      return a.index - b.index;
    })
    .map((entry) => entry.item);
}

export function pickMostUsedIds<T>(
  items: readonly T[],
  getId: (item: T) => string,
  recentIds: readonly string[],
  usageCount: (item: T) => number,
  limit: number = MOST_USED_GROUP_LIMIT,
): Set<string> {
  const present = new Set(items.map(getId));
  const byUsage = [...items]
    .filter((item) => usageCount(item) > 0)
    .sort((a, b) => usageCount(b) - usageCount(a))
    .map(getId);

  const ordered: string[] = [];
  for (const id of recentIds) {
    if (present.has(id) && !ordered.includes(id)) ordered.push(id);
  }
  for (const id of byUsage) {
    if (!ordered.includes(id)) ordered.push(id);
  }
  return new Set(ordered.slice(0, limit));
}

export function orderByMostUsed<T>(
  items: readonly T[],
  mostUsedIds: ReadonlySet<string>,
  recentIds: readonly string[],
  getId: (item: T) => string,
): T[] {
  if (mostUsedIds.size === 0) return [...items];
  const head: T[] = [];
  const tail: T[] = [];
  for (const item of items) {
    if (mostUsedIds.has(getId(item))) head.push(item);
    else tail.push(item);
  }
  return [...orderByMru(head, recentIds, getId), ...tail];
}

export function orderDropdownOptions<T>(
  items: readonly T[],
  options: {
    query?: string;
    recentIds?: readonly string[];
    getId: (item: T) => string;
    getLabel?: (item: T) => string;
    usageCount?: (item: T) => number;
  },
): T[] {
  const query = options.query?.trim() ?? '';
  if (query !== '') {
    if (options.getLabel) return rankByQueryRelevance(items, query, options.getLabel);
    return [...items];
  }
  const recentIds = options.recentIds ?? [];
  const usageCount = options.usageCount ?? (() => 0);
  const mostUsedIds = pickMostUsedIds(items, options.getId, recentIds, usageCount);
  return orderByMostUsed(items, mostUsedIds, recentIds, options.getId);
}

export function dropdownGroupLabel<T>(
  item: T,
  options: {
    query?: string;
    mostUsedIds: ReadonlySet<string>;
    getId: (item: T) => string;
    mostUsedLabel: string;
  },
): string {
  if ((options.query?.trim() ?? '') !== '') return '';
  if (options.mostUsedIds.size === 0) return '';
  return options.mostUsedIds.has(options.getId(item)) ? options.mostUsedLabel : '';
}

export function useDropdownMru(selector: string, max: number = DROPDOWN_MRU_MAX) {
  const userId = useAuthStore((state) => state.user?.id);
  const tenantId = useAuthStore((state) => state.user?.tenantId);
  const storageKey =
    userId !== undefined && tenantId !== undefined && userId !== '' && tenantId !== ''
      ? dropdownMruStorageKey(selector, userId, tenantId)
      : '';

  const [recentIds, setRecentIds] = useState<string[]>(() =>
    storageKey === '' ? [] : readMruIds(storageKey),
  );

  useEffect(() => {
    setRecentIds(storageKey === '' ? [] : readMruIds(storageKey));
  }, [storageKey]);

  const record = useCallback(
    (id: string) => {
      if (id === '' || storageKey === '') return;
      setRecentIds((prev) => {
        const next = recordMruId(prev, id, max);
        writeMruIds(storageKey, next);
        return next;
      });
    },
    [storageKey, max],
  );

  const order = useCallback(
    <T,>(
      items: readonly T[],
      getId: (item: T) => string,
      query?: string,
      getLabel?: (item: T) => string,
      usageCount?: (item: T) => number,
    ): T[] => {
      if (ALPHABETICAL_ONLY_MRU_SELECTORS.has(selector)) return [...items];
      return orderDropdownOptions(items, {
        query,
        recentIds,
        getId,
        ...(getLabel !== undefined ? { getLabel } : {}),
        ...(usageCount !== undefined ? { usageCount } : {}),
      });
    },
    [recentIds, selector],
  );

  return { recentIds, record, order, storageKey };
}
